import { Bar, Cyl, Lathe, Leaf, Sphere } from '../../render/parts'
import type { Tone } from '../../render/theme'

/** Plants. Foliage is flat double-sided blades — cheap, and it reads correctly at isometric scale. */

/** Deterministic pseudo-random so a given plant always looks identical. */
function noise(index: number, seed: number): number {
  const x = Math.sin(index * 127.1 + seed * 311.7) * 43758.5453
  return x - Math.floor(x)
}

export interface PotProps {
  /** Radius at the rim. */
  r?: number
  height?: number
  tone?: Tone
  /** Straight-sided cylinder instead of the default tapered pot. */
  straight?: boolean
  soilTone?: Tone
}

export function Pot({ r = 0.1, height = 0.13, tone = 'shell', straight = false, soilTone = 'soil' }: PotProps) {
  const base = straight ? r : r * 0.72
  const profile: [number, number][] = [
    [0, 0],
    [base, 0],
    [base, 0.012],
    [r, height - 0.018],
    [r, height],
    [r - 0.012, height],
    [r - 0.012, height - 0.02],
    [base - 0.01, 0.02],
    [0, 0.02],
  ]
  return (
    <group>
      <Lathe tone={tone} profile={profile} segments={26} profileKey={`pot:${r}:${height}:${straight}`} />
      <Cyl tone={soilTone} r={r - 0.016} h={0.012} position={[0, height - 0.024, 0]} segments={26} />
    </group>
  )
}

export interface LeafSprayProps {
  count?: number
  /** Y of the point the leaves spring from. */
  origin?: number
  length?: number
  width?: number
  minTilt?: number
  maxTilt?: number
  seed?: number
  tone?: Tone
  /** Draws a stem from the origin to each leaf base. */
  stems?: boolean
  spread?: number
}

/** A radial spray of blades — the generic foliage primitive. */
export function LeafSpray({
  count = 9,
  origin = 0.12,
  length = 0.22,
  width = 0.07,
  minTilt = 0.25,
  maxTilt = 1.05,
  seed = 1,
  tone = 'foliage',
  stems = false,
  spread = 0,
}: LeafSprayProps) {
  return (
    <group>
      {Array.from({ length: count }, (_, index) => {
        const azimuth = (index / count) * Math.PI * 2 + noise(index, seed) * 0.5
        const tilt = minTilt + noise(index + 41, seed) * (maxTilt - minTilt)
        const scale = 0.78 + noise(index + 97, seed) * 0.42
        const stemLength = spread * (0.5 + noise(index + 7, seed) * 0.5)
        const baseY = origin + Math.cos(tilt) * stemLength
        const baseR = Math.sin(tilt) * stemLength
        return (
          <group key={index} rotation={[0, azimuth, 0]}>
            {stems && stemLength > 0.001 ? (
              <Bar
                tone={tone}
                r={0.006}
                segments={6}
                from={[0, origin, 0]}
                to={[0, baseY, baseR]}
              />
            ) : null}
            <group position={[0, baseY, baseR]} rotation={[tilt, 0, 0]}>
              <Leaf tone={tone} length={length * scale} width={width * scale} />
            </group>
          </group>
        )
      })}
    </group>
  )
}

export type PlantVariant = 'desk' | 'bushy' | 'tall' | 'blades'

export interface PottedPlantProps {
  variant?: PlantVariant
  potTone?: Tone
  foliageTone?: Tone
  seed?: number
}

/**
 * Default leaf tone per silhouette, mirroring how the reference illustrations
 * treat greenery: big floor plants are drawn as white blades with a contour
 * (a charcoal mass that size reads as a hole in the page), while small desk
 * plants stay solid charcoal as a deliberate dark accent.
 */
function defaultFoliage(variant: PlantVariant): Tone {
  return variant === 'tall' || variant === 'bushy' ? 'shellSoft' : 'foliage'
}

/**
 * Catalog-ready plant. Four silhouettes cover almost every office render:
 * a small desk plant, a bushy floor plant, a tall leafy one and a snake plant.
 */
export function PottedPlant({
  variant = 'desk',
  potTone = 'shell',
  foliageTone,
  seed = 3,
}: PottedPlantProps) {
  const leafTone = foliageTone ?? defaultFoliage(variant)

  if (variant === 'tall') {
    const potHeight = 0.28
    const potR = 0.16
    return (
      <group>
        <Pot r={potR} height={potHeight} tone={potTone} />
        <Bar tone={leafTone} r={0.012} from={[0, potHeight - 0.03, 0]} to={[0.01, potHeight + 0.34, -0.01]} />
        <LeafSpray
          count={9}
          origin={potHeight + 0.16}
          length={0.34}
          width={0.19}
          minTilt={0.35}
          maxTilt={1.25}
          spread={0.3}
          stems
          seed={seed}
          tone={leafTone}
        />
        <LeafSpray
          count={5}
          origin={potHeight + 0.44}
          length={0.28}
          width={0.16}
          minTilt={0.15}
          maxTilt={0.7}
          spread={0.2}
          stems
          seed={seed + 11}
          tone={leafTone}
        />
      </group>
    )
  }

  if (variant === 'bushy') {
    const potHeight = 0.24
    return (
      <group>
        <Pot r={0.15} height={potHeight} tone={potTone} straight />
        <Sphere tone={leafTone} r={0.02} position={[0, potHeight + 0.04, 0]} />
        <LeafSpray
          count={14}
          origin={potHeight + 0.02}
          length={0.3}
          width={0.11}
          minTilt={0.3}
          maxTilt={1.35}
          spread={0.12}
          seed={seed}
          tone={leafTone}
        />
      </group>
    )
  }

  if (variant === 'blades') {
    const potHeight = 0.26
    return (
      <group>
        <Pot r={0.13} height={potHeight} tone={potTone} straight />
        <LeafSpray
          count={11}
          origin={potHeight - 0.01}
          length={0.62}
          width={0.075}
          minTilt={0.06}
          maxTilt={0.34}
          seed={seed}
          tone={leafTone}
        />
      </group>
    )
  }

  const potHeight = 0.12
  return (
    <group>
      <Pot r={0.085} height={potHeight} tone={potTone} />
      <LeafSpray
        count={9}
        origin={potHeight}
        length={0.19}
        width={0.062}
        minTilt={0.22}
        maxTilt={1.1}
        seed={seed}
        tone={leafTone}
      />
    </group>
  )
}
